import { useEffect, useRef } from "react";

/**
 * The one modal in the console.
 *
 * Anything that used to expand a form inline underneath a button goes through
 * here instead, so a form that interrupts you looks like it interrupted you
 * rather than shifting the page under your cursor.
 *
 * Three things it does that a plain div does not, and that are easy to forget
 * when hand-rolling the next one:
 *
 *   - Escape closes it, because that is what every other dialog on the
 *     machine does.
 *   - Focus moves into the panel on open, so a keyboard user is not left
 *     behind on the button they just pressed.
 *   - The backdrop closes on a click, but only when the click started on the
 *     backdrop — otherwise a text selection that ends outside the panel
 *     throws away what someone just typed.
 */
export function Modal({
  title,
  description,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const pressedBackdrop = useRef(false);

  // `onClose` is an inline arrow at every call site, so it is a new function on
  // every render. Depending on it here re-ran this effect on each keystroke,
  // and the focus() call below then dragged the cursor out of whatever field
  // was being typed into. Keep it in a ref: the listener stays stable, and the
  // effect below runs exactly once.
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeRef.current();
    }
    document.addEventListener("keydown", onKey);

    // Focus the panel once, on open. A keyboard user should land inside the
    // dialog rather than on the button that opened it — but only when it
    // appears, never again while they are using it.
    panel.current?.focus();

    // The page behind must not scroll while a modal is open.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => { pressedBackdrop.current = e.target === e.currentTarget; }}
      onMouseUp={(e) => {
        if (pressedBackdrop.current && e.target === e.currentTarget) onClose();
        pressedBackdrop.current = false;
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
      >
        <div className="modal-head">
          <div>
            <h2>{title}</h2>
            {description && <p className="muted small">{description}</p>}
          </div>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
